export interface HealthResponse {
  status: string;
  config: {
    liveTrading?: boolean;
    hyperliquidEnv?: string;
    hasAccountAddress?: boolean;
    hasAgentPrivateKey?: boolean;
    llmModel?: string;
  };
}

export interface PortfolioResponse {
  observedAt: string;
  accountAddress?: string;
  userState?: {
    marginSummary?: {
      accountValue?: string;
      totalMarginUsed?: string;
    };
    withdrawable?: string;
    assetPositions?: Array<{
      position?: {
        coin?: string;
        szi?: string;
        leverage?: {
          type?: string;
          value?: number;
        };
        entryPx?: string;
        positionValue?: string;
        unrealizedPnl?: string;
        returnOnEquity?: string;
        liquidationPx?: string | null;
        marginUsed?: string;
      };
    }>;
  };
  openOrders?: Array<{
    coin?: string;
    side?: string;
    sz?: string;
    limitPx?: string;
  }>;
}

export interface LedgerSummaryResponse {
  observedAt: string;
  executionReports: number;
  fills: number;
  statusCounts: Record<string, number>;
  totalClosedPnl: number;
  totalFees: number;
  recentExecutions: Array<{
    intentId: string;
    noteId?: string;
    status: string;
    rawStatus?: string;
    reportedAt?: string;
    lastOpenOrderSeenAt?: string;
    cloid?: string;
    symbol?: string;
    side?: string;
    size?: string;
    reduceOnly?: boolean;
    closedPnl: number;
    fees: number;
    fillCount: number;
    openOrderCount: number;
    fillResult?: {
      size: string;
      avgPx: string;
      oid: number;
    };
    thesis?: string;
  }>;
}

export interface SubagentsResponse {
  subagents: Array<{
    id: string;
    mission: string;
    toolAccess: string[];
    identity?: {
      name?: string;
      gender?: "female" | "male";
      appearance?: {
        hair?: "short" | "bob" | "long";
        outfit?: "jacket" | "suit" | "hoodie" | "vest";
        accessory?: "none" | "glasses";
      };
      stateNote?: string;
      workloadPressure?: number;
      contributionNotes?: string[];
      rewardNotes?: string[];
    };
    status: string;
    createdBy: string;
    createdAt?: string;
    lastActivityAt?: string;
    redirections?: Array<{
      redirectedAt: string;
      redirectedBy: string;
      previousMission: string;
      mission: string;
      toolAccess: string[];
      reason: string;
    }>;
    terminatedReason?: string;
    reports?: Array<{
      reportedAt: string;
      reportedBy: string;
      summary: string;
      artifacts: string[];
      nextActions: string[];
    }>;
  }>;
}

export interface ExecutionDelegateResponse {
  executionDelegate: {
    subagentId: string;
    assignedAt: string;
    assignedBy: string;
    reason: string;
    modelConfig?: {
      provider?: string;
      model?: string;
      reasoning?: string;
      purpose?: string;
      assignedBy?: string;
      assignedAt?: string;
    };
  } | null;
  runtimePhase2ModelConfig?: {
    provider?: string;
    model?: string;
    experimentProvider?: string;
    experimentModel?: string;
    experimentRate?: number;
    source?: string;
  };
}

export interface CompanyEventsResponse {
  events: Array<{
    observedAt: string;
    eventType:
      | "subagent_hired"
      | "subagent_redirected"
      | "subagent_terminated"
      | "execution_delegate_assigned"
      | "execution_delegate_revoked"
      | "subagent_identity_updated";
    actor: string;
    targetId?: string;
    targetName?: string;
    summary?: string;
    reason?: string;
  }>;
}

export interface TeamMessagesResponse {
  messages: Array<{
    id: string;
    createdAt: string;
    from: string;
    to?: string;
    replyTo?: string;
    subject: string;
    body: string;
    priority: "low" | "normal" | "high";
    requiresResponse: boolean;
    handledBy?: string[];
  }>;
}

export interface ProposalsResponse {
  proposals: Array<{
    id: string;
    createdAt: string;
    proposerId: string;
    proposalType: "trade" | "research" | "process" | "hire" | "terminate" | "authority" | "other";
    thesis: string;
    requestedAction: string;
    knownRisks?: string;
    status: "open" | "approved" | "rejected" | "closed";
    decision?: {
      decidedAt: string;
      decidedBy: string;
      decision: "approved" | "rejected" | "closed";
      reason: string;
    };
  }>;
}

export interface SourceReliabilityResponse {
  generatedAt: string;
  totalSources: number;
  sources: Array<{
    source: string;
    mentions?: number;
    confidence?: number;
    assets?: string[];
    suggestedUse?: string;
  }>;
}

export interface AlertCandidatesResponse {
  freshnessHours?: number;
  includeStale?: boolean;
  staleOmitted?: number;
  alerts: Array<{
    id: string;
    observedAt: string;
    receivedAt: string;
    sourceType: "telegram" | "x" | "rss" | "api" | "websocket" | "manual";
    sourceClass?:
      | "official"
      | "original"
      | "relay"
      | "chatter"
      | "stale"
      | "inaccessible"
      | "context_only";
    sourceId: string;
    sourceName?: string;
    messageId?: string;
    normalizedText?: string;
    rawTextRedacted?: string;
    assets: string[];
    catalystType: string;
    sentiment: string;
    urgency: "low" | "medium" | "high";
    links: string[];
    candidateScore: number;
    ageHours?: number | null;
    isFresh?: boolean;
    freshness?: "fresh" | "stale";
  }>;
}

export const EVENT_TRUTH_GATE = {
  minGateCells: 40,
  minEpisodeBlocks: 10,
  minDistinctSymbols: 5,
  minJoinedCoveragePct: 50,
  minPublisherCoveragePct: 80,
  minClockValidityPct: 99,
} as const;

export type EventTruthPillarStage =
  | "unavailable"
  | "accumulate"
  | "join_wait"
  | "first_join"
  | "gate_cells"
  | "gate_episodes"
  | "gate_symbols"
  | "gate_coverage"
  | "gate_clocks"
  | "gate_publisher"
  | "gate_halves"
  | "gate_concentration"
  | "gate_bootstrap"
  | "gate_economic"
  | "pass_shadow"
  | "falsified";

export interface EventTruthTapeEvaluationCandidate {
  candidateId: string;
  status: string;
  verdictCells?: number;
  joinedCells?: number;
  gateCells?: number;
  episodeBlocks?: number;
  distinctSymbols?: number;
  joinedCoveragePct?: number | null;
  holdReasonPrimary?: string;
  holdReason?: string;
}

export interface EventTruthTapeStatusResponse {
  observerOnly: true;
  observedAt: string;
  available: boolean;
  reason?: string;
  researchDir: string;
  tape?: {
    totalLines: number;
    totalBytes: number;
    shardCount: number;
    newestMtimeIso?: string;
    symbols: string[];
  };
  registry?: {
    candidateCount: number;
    candidates: Array<{ candidateId: string; registeredAt: string | null; kind: string | null }>;
  };
  evaluations?: {
    evaluatedAt?: string;
    candidates: EventTruthTapeEvaluationCandidate[];
  };
  note: string;
}

export interface AlertIngestHealthResponse {
  lastTickAt?: string;
  okCount: number;
  failingCount: number;
  cooldownCount: number;
  disabledCount: number;
  reviewSources: string[];
  sources: Array<{
    source: string;
    status: "ok" | "error" | "cooldown" | "disabled";
    recorded?: number;
    duplicate?: number;
    items?: number;
    consecutiveFailures?: number;
    nextRetryAt?: string;
    lastError?: string;
  }>;
}

export interface CollaborationNoteSummaryForDashboard {
  id: string;
  category: "entry" | "exit" | "review";
  status: "open" | "summarized" | "decided" | "closed";
  createdAt: string;
  closedAt?: string;
  triggers?: string[];
  positionSnapshot?: {
    symbol?: string;
  };
  verdict?: {
    decision: string;
    reason?: string;
    decidedAt?: string;
    decidedBy?: string;
  };
  executionResult?: {
    status?: string;
    intentId?: string;
  };
  ceoReview?: Array<{
    reviewedAt?: string;
    note?: string;
    actions?: string[];
    sourceUpdates?: Array<{
      sourceId?: string;
      action?: string;
      reason?: string;
    }>;
    catalystCalibration?: Array<{
      catalystType?: string;
      outcomeLabel?: string;
      lesson?: string;
    }>;
  }>;
  entryContext?: {
    sourceType?: "telegram" | "x" | "rss" | "api" | "websocket" | "manual";
    sourceClass?:
      | "official"
      | "original"
      | "relay"
      | "chatter"
      | "stale"
      | "inaccessible"
      | "context_only";
    assets?: string[];
  };
}

export interface CollaborationNotesResponse {
  notes: CollaborationNoteSummaryForDashboard[];
  closedAwaitingCeoReviewCount?: number;
  closedReviewedCount?: number;
  learningSignalCount?: number;
}

export interface RuntimeStatusResponse {
  xRumorBudget?: {
    observedAt?: string;
    allowed?: boolean;
    reason?: string;
    pressure?: string;
    mode?: string;
    stateStatus?: string;
    cooldownUntil?: string;
    manualHalt: boolean;
    review: "clear" | "manual_halt" | "blocked" | "tight" | "unknown";
  };
  xaiQuotaEstimate?: {
    source: string;
    authoritativeProviderQuota: false;
    observedAt?: string;
    projectedDaysRemaining?: number;
    windows: {
      fiveHour: { used: number; limit: number; percent: number };
      day: { used: number; limit: number; percent: number };
      month: { used: number; limit: number; percent: number };
    };
    confidence: {
      localTelemetry: "low" | "medium";
      providerQuota: "low";
    };
  };
}

export type ReviewReadinessStatus =
  | "counting"
  | "prep"
  | "ready"
  | "diagnostic"
  | "awaiting_ship"
  | "unknown";

export type WeakSliceExhaustionThresholds = {
  stallHours: number;
  minRtsAfterLastWeak: number;
  minCaptureFraction: number;
  minCaptureAtMinRt: number;
};

export type ReviewReadinessLayer = {
  actual: number | null;
  target?: number | null;
  required?: number;
  remaining: number | null;
  percent: number | null;
  met: boolean;
  stallCapTarget?: number | null;
  usesWeakIc0Slice?: boolean;
  qualityBasis?: "weak_ic0_slice" | "total_rt" | "total_rt_after_weak_exhaustion";
  weakSliceExhausted?: boolean;
  bindingActual?: number | null;
  lastWeakIc0RoundTripAt?: string | null;
  roundTripsSinceLastWeakIc0?: number | null;
  stallHoursSinceLastWeakIc0?: number | null;
  exhaustionThresholds?: WeakSliceExhaustionThresholds;
};

export type ReviewReadiness = {
  protocolLabel: string;
  status: ReviewReadinessStatus;
  statusLabelKo: string;
  bindingReady: boolean;
  diagnosticEligible: boolean;
  fillLayer: ReviewReadinessLayer;
  qualityLayer: ReviewReadinessLayer;
  regimeLayer: {
    contradictionRate24h: number | null;
    crisis: boolean;
    stable: boolean | null;
  };
  detailKo: string;
  metaKo?: string;
  metricsSource?: string;
};

export interface CycleOperatorSummary {
  protocolVersion: string;
  createdAfter: string | null;
  operatingMode: string;
  entryGuardMode: string;
  entrySubmissionFreeze?: boolean;
  legacy500Retired: boolean;
  fills: number | null;
  fillsMetric: "protocol_entry_fills" | "legacy_natural_trades" | null;
  fillsSource: "ledger_live" | "handoff_snapshot" | "authoritative_guard" | null;
  fillsDiscrepancy: number | null;
  regressionGuardActive?: boolean;
  fillsFromHandoff?: number | null;
  fillsFromLedger?: number | null;
  fillsUntilReview: number | null;
  nextReviewAtFills: number | null;
  fillsPerReview: number;
  improvementId: string | null;
  pendingBundleId: string | null;
  improvementPhase: string | null;
  lastReviewGateFills?: number | null;
  fillsSinceLastImprovement?: number | null;
  /** @deprecated Use atFillGate — fill count only, not RT quality binding. */
  atReview: boolean;
  /** Fill gate reached (nextReviewAtFills). */
  atFillGate: boolean;
  /** Fill + RT quality binding — formal review adjudication gate. */
  atBindingReview: boolean;
  fillCountMissingNoteIds?: number;
  planDoc: string | null;
  briefLinesKo: string[];
  roundTripNetBps: number | null;
  handoffUpdatedAt: string | null;
  portfolioCoupling?: {
    intentsTotal: number | null;
    burstsGte5: number | null;
    burstCap4Over: number | null;
    roundTripNetBps: number | null;
    generatedAt: string | null;
  } | null;
  bundle4Shadow?: {
    wouldBlockDeduped: number | null;
    wouldBlockRaw: number | null;
    gateMin: number;
    improvementId: string;
  } | null;
  bundle5Shadow?: {
    wouldBlockDeduped: number | null;
    wouldBlockRaw: number | null;
    gateMin: number;
    improvementId: string;
  } | null;
  reviewReadiness?: ReviewReadiness | null;
  bundle20ExitEnforce?: {
    improvementId: string;
    knob: string;
    operatingMode: "enforce";
    sinceIso: string | null;
    events: number;
    byKind: {
      planRefreshAdmitted: number;
      writebackApplied: number;
      postTradeRefresh: number;
      thesisDecay: number;
      domainPanelReplace: number;
      weakScoutV15: number;
      stoplossDefer: number;
      ladderFrontload: number;
    };
  } | null;
}

export interface EntryObservabilityResponse {
  generatedAt?: string;
  window?: {
    limit?: number;
    createdAfter?: string | null;
    noteCount?: number;
    fetchedNoteCount?: number;
    noteLimitHit?: boolean;
    ledgerLimit?: number;
    ledgerExecutionCount?: number;
    ledgerLimitHit?: boolean;
    unlinkedExecution?: number;
  };
  naturalTradeProgress?: {
    requiredTrades: number;
    actualTrades: number;
    remainingTrades: number;
    complete: boolean;
    excluded?: {
      unlinkedExecution?: number;
    };
  };
  noTradeCauseBreakdown?: Record<string, number>;
  noTradeCauseBreakdownWindows?: Record<
    string,
    {
      createdAfter?: string;
      terminalNoTradeDecisions: number;
      noTradeCauseBreakdown: Record<string, number>;
    }
  >;
  exitWorkflow?: {
    terminalExitNotes: number;
    hardExitCount: number;
    hardExitRate: number;
    hardExitByTrigger: Record<string, number>;
    reviewOnlyHoldCount: number;
    delegateIntentExitCount: number;
    executionResultCount: number;
    recoveredExecutionExitCount: number;
    delegateTextExitCount: number;
    flatPositionExitCount: number;
    unclassifiedExecutionExitCount: number;
  };
  exitPlanQuality?: {
    applicableEntryDecisions: number;
    withExitPlan: number;
    missingExitPlan: number;
    count: number;
    structuredFieldCountP50: number;
    structuredFieldCountAvg: number;
    zeroStructuredFieldCount: number;
    structuredFieldCountHistogram: Record<string, number>;
    fieldPresence: Record<string, number>;
    decisionBreakdown: Record<
      string,
      {
        count: number;
        structuredFieldCountP50: number;
        structuredFieldCountAvg: number;
        zeroStructuredFieldCount: number;
      }
    >;
    windows?: Record<
      string,
      {
        createdAfter?: string;
        applicableEntryDecisions: number;
        withExitPlan: number;
        missingExitPlan: number;
        count: number;
        structuredFieldCountP50: number;
        structuredFieldCountAvg: number;
        zeroStructuredFieldCount: number;
        structuredFieldCountHistogram: Record<string, number>;
        fieldPresence: Record<string, number>;
        decisionBreakdown: Record<
          string,
          {
            count: number;
            structuredFieldCountP50: number;
            structuredFieldCountAvg: number;
            zeroStructuredFieldCount: number;
          }
        >;
      }
    >;
  };
  entryTempo?: {
    windows: Record<
      string,
      {
        entryNotes: number;
        enterVerdicts: number;
        withholdVerdicts: number;
        rescheduleVerdicts?: number;
        scoutVerdicts?: number;
        uniqueFilledEntries: number;
        noFillReports?: number;
        entryNotesPerHour: number;
        uniqueFilledEntriesPerHour: number;
      }
    >;
    frequencyWarnings: string[];
    followUpRequired: boolean;
  };
}

export interface DataHealthOffenseHorizon {
  horizon: string;
  n: number | null;
  evMeanBps: number | null;
  evLowerBps: number | null;
  qualifiedCohorts: number;
}

export interface DataHealthSnapshot {
  schemaVersion?: string;
  generatedAt?: string;
  topStatus?: "healthy" | "degraded" | "unknown" | "stale";
  effectiveTopStatus?: "healthy" | "degraded" | "unknown" | "stale";
  streamStatus?: string;
  streams?: Array<{
    key?: string;
    label?: string;
    status?: string;
    ageSeconds?: number | null;
    rowsToday?: number | null;
    note?: string;
  }>;
  runtime?: {
    wsUp?: boolean;
    runtimeInstances?: number;
  };
  alertIngest?: {
    available?: boolean;
    lastTickAt?: string | null;
    failingCount?: number | null;
    okCount?: number | null;
  };
  offense?: {
    measurementState?: "success" | "error" | "stale" | "unknown" | "skipped";
    barMet?: boolean | null;
    totalQualifiedCohorts?: number;
    feeBps?: number;
    bestHorizon?: DataHealthOffenseHorizon | null;
    byHorizon?: DataHealthOffenseHorizon[];
    lastSuccessAt?: string | null;
    lastError?: string | null;
  };
  corpusGrowth?: Array<{ date: string; count: number }>;
  corpusObservation?: { observed?: string; hypothesis?: string | null };
  lanesNote?: { observed?: string; generatedAt?: string | null; basis?: string | null };
  verdict?: { line?: string; topStatus?: string };
}

export interface DataHealthTrajectoryPoint {
  observedAt?: string;
  streamStatus?: string;
  topStatus?: string;
  barMet?: boolean | null;
  byHorizonLB?: Array<{ h: string; lb: number | null; n: number | null }>;
}

export interface DataHealthResponse {
  observerOnly: true;
  observedAt: string;
  available: boolean;
  reason?: string;
  snapshotPath?: string;
  effectiveTopStatus?: "healthy" | "degraded" | "unknown" | "stale";
  snapshot?: DataHealthSnapshot;
  trajectory?: DataHealthTrajectoryPoint[];
  note: string;
}

export interface DashboardData {
  health?: HealthResponse;
  portfolio?: PortfolioResponse;
  ledger?: LedgerSummaryResponse;
  subagents?: SubagentsResponse;
  executionDelegate?: ExecutionDelegateResponse;
  companyEvents?: CompanyEventsResponse;
  teamMessages?: TeamMessagesResponse;
  proposals?: ProposalsResponse;
  sources?: SourceReliabilityResponse;
  alertCandidates?: AlertCandidatesResponse;
  alertIngestHealth?: AlertIngestHealthResponse;
  collaborationNotes?: CollaborationNotesResponse;
  runtimeStatus?: RuntimeStatusResponse;
  eventTruthTape?: EventTruthTapeStatusResponse;
  dataHealth?: DataHealthResponse;
  entryObservability?: EntryObservabilityResponse;
  errors?: {
    critical?: Record<string, string>;
    background?: Record<string, string>;
  };
}

export interface DashboardState {
  data: DashboardData;
  loading: boolean;
  error?: string;
  refreshedAt?: string;
}
